import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import Button from '@/ui/_lib/Button';
import svgPaths from './svg-paths';
import { ReactComponent as ArrowIcon } from '@/ui/arrow.svg'; // Fix import for Vite with SVGR

// DonutChart component
function DonutChart({
  value,
  label,
  malePercentage,
  femalePercentage,
}: {
  value: string;
  label: string;
  malePercentage: string;
  femalePercentage: string;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-5">
      <div className="flex flex-col items-center text-center">
        <div className="text-[16px] font-bold text-[#475467]">{value}</div>
        <div className="text-[14px] text-[#667085]">{label}</div>
      </div>
      <div className="flex w-full items-center justify-center gap-8">
        <div className="flex flex-col items-end">
          <div className="text-[13px] font-bold text-[#475467]">{malePercentage}</div>
          <div className="text-[12px] text-[#667085]">Male</div>
        </div>
        <div className="relative size-[81px]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 83 83">
            <path d={svgPaths.p1fdd3d80} fill="#039BE5" stroke="white" strokeWidth="0.430851" />
            <path d={svgPaths.p3dbb4e80} fill="#FB7F65" stroke="white" strokeWidth="0.430851" />
          </svg>
        </div>
        <div className="flex flex-col items-start">
          <div className="text-[13px] font-bold text-[#475467]">{femalePercentage}</div>
          <div className="text-[12px] text-[#667085]">Female</div>
        </div>
      </div>
    </div>
  );
}

// FilterTabs component
interface FilterTabsProps {
  activeTab: string;
  tabs: string[];
  onTabChange: (tab: string) => void; // eslint-disable-line no-unused-vars
}

function FilterTabs({ activeTab, tabs, onTabChange }: FilterTabsProps) {
  return (
    <div className="flex gap-3">
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onTabChange(tab)}
          className={`rounded-[12px] px-3 py-2 text-[12px] transition-colors ${
            activeTab === tab
              ? 'bg-[#edf0f5] text-[#333333]'
              : 'border border-[#bdbdbd] bg-white text-[#333333] hover:bg-gray-50'
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

// ProgressBar component
function ProgressBar({ label, percentage, width = '33px' }: { label: string; percentage: string; width?: string }) {
  return (
    <div className="flex w-full items-end gap-2">
      <div className="flex flex-1 flex-col">
        <div className="mb-1 text-[12px] text-[#667085]">{label}</div>
        <div className="h-1.5 w-full overflow-hidden rounded-[4px] bg-[#f4f5f7]">
          <div className="h-full bg-[#039be5]" style={{ width }} />
        </div>
      </div>
      <div className="w-[29px] text-center text-[12px] text-[#333333]">{percentage}</div>
    </div>
  );
}

// Mock data for different links
const linkData = {
  overall: {
    clicks: { value: '12,054', male: '42%', female: '59%' },
    ageRange: [
      { label: '18 - 24', percentage: '1.0 %', width: '33px' },
      { label: '35 - 34', percentage: '1.0 %', width: '33px' },
      { label: '45 - 54', percentage: '1.0 %', width: '33px' },
      { label: '55 - 64', percentage: '1.0 %', width: '33px' },
      { label: '65 +', percentage: '1.0 %', width: '33px' },
    ],
    topLocation: [
      { label: 'Kathmandu', percentage: '1.0 %', width: '33px' },
      { label: 'Delhi', percentage: '1.0 %', width: '33px' },
      { label: 'Kolkata', percentage: '1.0 %', width: '33px' },
      { label: 'Dhaka', percentage: '1.0 %', width: '33px' },
      { label: 'Mumbai', percentage: '1.0 %', width: '33px' },
    ],
  },
  link1: {
    clicks: { value: '3,241', male: '38%', female: '62%' },
    ageRange: [
      { label: '18 - 24', percentage: '25 %', width: '25%' },
      { label: '35 - 34', percentage: '20 %', width: '20%' },
      { label: '45 - 54', percentage: '18 %', width: '18%' },
      { label: '55 - 64', percentage: '22 %', width: '22%' },
      { label: '65 +', percentage: '15 %', width: '15%' },
    ],
    topLocation: [
      { label: 'New York', percentage: '22 %', width: '22%' },
      { label: 'London', percentage: '18 %', width: '18%' },
      { label: 'Tokyo', percentage: '15 %', width: '15%' },
      { label: 'Sydney', percentage: '12 %', width: '12%' },
      { label: 'Berlin', percentage: '10 %', width: '10%' },
    ],
  },
  link2: {
    clicks: { value: '1,876', male: '55%', female: '45%' },
    ageRange: [
      { label: '18 - 24', percentage: '30 %', width: '30%' },
      { label: '35 - 34', percentage: '25 %', width: '25%' },
      { label: '45 - 54', percentage: '20 %', width: '20%' },
      { label: '55 - 64', percentage: '15 %', width: '15%' },
      { label: '65 +', percentage: '10 %', width: '10%' },
    ],
    topLocation: [
      { label: 'Paris', percentage: '25 %', width: '25%' },
      { label: 'Rome', percentage: '20 %', width: '20%' },
      { label: 'Madrid', percentage: '18 %', width: '18%' },
      { label: 'Vienna', percentage: '15 %', width: '15%' },
      { label: 'Prague', percentage: '12 %', width: '12%' },
    ],
  },
  link3: {
    clicks: { value: '2,543', male: '48%', female: '52%' },
    ageRange: [
      { label: '18 - 24', percentage: '15 %', width: '15%' },
      { label: '35 - 34', percentage: '25 %', width: '25%' },
      { label: '45 - 54', percentage: '30 %', width: '30%' },
      { label: '55 - 64', percentage: '20 %', width: '20%' },
      { label: '65 +', percentage: '10 %', width: '10%' },
    ],
    topLocation: [
      { label: 'Toronto', percentage: '20 %', width: '20%' },
      { label: 'Vancouver', percentage: '18 %', width: '18%' },
      { label: 'Montreal', percentage: '15 %', width: '15%' },
      { label: 'Calgary', percentage: '12 %', width: '12%' },
      { label: 'Ottawa', percentage: '10 %', width: '10%' },
    ],
  },
  link4: {
    clicks: { value: '4,123', male: '41%', female: '59%' },
    ageRange: [
      { label: '18 - 24', percentage: '22 %', width: '22%' },
      { label: '35 - 34', percentage: '28 %', width: '28%' },
      { label: '45 - 54', percentage: '19 %', width: '19%' },
      { label: '55 - 64', percentage: '17 %', width: '17%' },
      { label: '65 +', percentage: '14 %', width: '14%' },
    ],
    topLocation: [
      { label: 'Los Angeles', percentage: '19 %', width: '19%' },
      { label: 'Chicago', percentage: '16 %', width: '16%' },
      { label: 'Houston', percentage: '14 %', width: '14%' },
      { label: 'Phoenix', percentage: '13 %', width: '13%' },
      { label: 'Philadelphia', percentage: '11 %', width: '11%' },
    ],
  },
  link5: {
    clicks: { value: '3,876', male: '52%', female: '48%' },
    ageRange: [
      { label: '18 - 24', percentage: '18 %', width: '18%' },
      { label: '35 - 34', percentage: '24 %', width: '24%' },
      { label: '45 - 54', percentage: '26 %', width: '26%' },
      { label: '55 - 64', percentage: '21 %', width: '21%' },
      { label: '65 +', percentage: '11 %', width: '11%' },
    ],
    topLocation: [
      { label: 'Miami', percentage: '21 %', width: '21%' },
      { label: 'Seattle', percentage: '19 %', width: '19%' },
      { label: 'Boston', percentage: '17 %', width: '17%' },
      { label: 'Denver', percentage: '15 %', width: '15%' },
      { label: 'Atlanta', percentage: '13 %', width: '13%' },
    ],
  },
  link6: {
    clicks: { value: '2,987', male: '39%', female: '61%' },
    ageRange: [
      { label: '18 - 24', percentage: '27 %', width: '27%' },
      { label: '35 - 34', percentage: '22 %', width: '22%' },
      { label: '45 - 54', percentage: '19 %', width: '19%' },
      { label: '55 - 64', percentage: '18 %', width: '18%' },
      { label: '65 +', percentage: '14 %', width: '14%' },
    ],
    topLocation: [
      { label: 'San Francisco', percentage: '23 %', width: '23%' },
      { label: 'Dallas', percentage: '18 %', width: '18%' },
      { label: 'Detroit', percentage: '15 %', width: '15%' },
      { label: 'Minneapolis', percentage: '13 %', width: '13%' },
      { label: 'Portland', percentage: '11 %', width: '11%' },
    ],
  },
  link7: {
    clicks: { value: '1,543', male: '46%', female: '54%' },
    ageRange: [
      { label: '18 - 24', percentage: '20 %', width: '20%' },
      { label: '35 - 34', percentage: '25 %', width: '25%' },
      { label: '45 - 54', percentage: '22 %', width: '22%' },
      { label: '55 - 64', percentage: '19 %', width: '19%' },
      { label: '65 +', percentage: '14 %', width: '14%' },
    ],
    topLocation: [
      { label: 'Austin', percentage: '24 %', width: '24%' },
      { label: 'Nashville', percentage: '20 %', width: '20%' },
      { label: 'Las Vegas', percentage: '17 %', width: '17%' },
      { label: 'Orlando', percentage: '14 %', width: '14%' },
      { label: 'San Diego', percentage: '12 %', width: '12%' },
    ],
  },
};

// Main Analytics component
export function AnalyticsPage({ onBack }: { onBack: () => void }) {
  const [ageRangeFilter, setAgeRangeFilter] = useState('All');
  const [locationFilter, setLocationFilter] = useState('Cities');
  const [linkFilter, setLinkFilter] = useState('overall');
  const [dateRange, setDateRange] = useState('Jul 26 - Aug 25'); // Add date range state
  const [isCustomDateRange, setIsCustomDateRange] = useState(false); // Track if custom date range is selected
  const [startDate, setStartDate] = useState('2023-07-26'); // Custom start date
  const [endDate, setEndDate] = useState('2023-08-25'); // Custom end date

  // Get data based on selected filter
  const currentData = linkData[linkFilter as keyof typeof linkData] || linkData.overall;

  // Date range options
  const dateRangeOptions = [
    'Jul 26 - Aug 25',
    'Jun 26 - Jul 25',
    'May 26 - Jun 25',
    'Apr 26 - May 25',
    'Mar 26 - Apr 25',
    'Feb 26 - Mar 25',
    'Jan 26 - Feb 25',
    'Custom Range', // Add custom range option
  ];

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle date range change
  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setDateRange(value);

    if (value === 'Custom Range') {
      setIsCustomDateRange(true);
    } else {
      setIsCustomDateRange(false);
    }
  };

  // Handle custom date changes
  const handleCustomDateChange = () => {
    if (startDate && endDate) {
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);
      setDateRange(`${formattedStartDate} - ${formattedEndDate}`);
      setIsCustomDateRange(false);
    }
  };

  const handleLinkFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLinkFilter(e.target.value);
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 pt-8 md:px-8 md:py-6">
        <button type="button" onClick={onBack}>
          <ArrowLeft className="size-4 text-[#333333] md:size-5" />
        </button>
        <h1 className="text-[14px] font-semibold text-[#333333] md:text-xl">Advanced Analytics</h1>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto px-4 pb-10 md:px-8">
        <div className="mx-auto max-w-sm space-y-4 md:max-w-6xl md:space-y-6">
          {/* Filters - Web optimized */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex h-8 w-full items-center rounded-[8px] border border-zinc-200 bg-white px-3 md:w-56 md:h-10">
              <select
                value={linkFilter}
                onChange={handleLinkFilterChange}
                className="w-full appearance-none bg-transparent text-[12px] outline-none md:text-sm"
              >
                <option value="overall">Overall</option>
                <option value="link1">Social Media Link</option>
                <option value="link2">Portfolio Website</option>
                <option value="link3">Blog Article</option>
                <option value="link4">Product Page</option>
                <option value="link5">Event Registration</option>
                <option value="link6">Newsletter Signup</option>
                <option value="link7">Contact Form</option>
              </select>
              <ArrowIcon className=" text-[#475467]" />
            </div>

            {/* Date range dropdown */}
            <div className="flex h-8 w-full items-center rounded-[8px] border border-zinc-200 bg-white px-3 md:w-56 md:h-10">
              <select
                value={dateRange}
                onChange={handleDateRangeChange}
                className="w-full appearance-none bg-transparent text-[12px] outline-none md:text-sm"
              >
                {dateRangeOptions.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
              <ArrowIcon className=" text-[#475467]" />
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {isCustomDateRange && (
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <div className="space-y-3">
                <div>
                  <input
                    id="start-date"
                    name="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    aria-label="Start Date"
                    className="mt-1 w-full rounded-[8px] border border-[#D0D5DD] p-2 text-[14px]"
                  />
                </div>
                <div>
                  <input
                    id="end-date"
                    name="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    aria-label="End Date"
                    className="mt-1 w-full rounded-[8px] border border-[#D0D5DD] p-2 text-[14px]"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCustomDateChange}
                  className="w-full rounded-[8px] bg-[#039BE5] py-2 text-[14px] text-white"
                >
                  Apply Date Range
                </button>
              </div>
            </div>
          )}

          {/* Conditional Card Display for Overall Filter */}
          {linkFilter === 'overall' && (
            /* Profile Visits Card - Shown only for Overall filter */
            <div className="rounded-lg bg-white p-5 shadow-sm">
              <DonutChart
                value={currentData.clicks.value}
                label="Profile Visits"
                malePercentage={currentData.clicks.male}
                femalePercentage={currentData.clicks.female}
              />
            </div>
          )}

          {/* Conditional Card Display for Individual Links */}
          {linkFilter !== 'overall' && (
            /* Link Clicked Card - Shown for individual links */
            <div className="rounded-lg bg-white p-5 shadow-sm">
              <DonutChart
                value={currentData.clicks.value}
                label="Link clicked"
                malePercentage={currentData.clicks.male}
                femalePercentage={currentData.clicks.female}
              />
            </div>
          )}

          {/* Age Range and Top Location Cards - Mobile Horizontal Scroll, Web Grid */}
          <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0">
            {/* Age Range Card */}
            <div className="w-[280px] sm:w-[240px] flex-none space-y-5 rounded-lg bg-white p-4 shadow-sm min-w-[200px] md:w-auto md:flex-1">
              <h3 className="text-[16px] font-bold text-[#475467] md:text-lg">Age Range</h3>

              <FilterTabs
                activeTab={ageRangeFilter}
                tabs={['All', 'Men', 'Women']}
                onTabChange={(tab) => setAgeRangeFilter(tab)}
              />

              <div className="space-y-3">
                {currentData.ageRange.map((item) => (
                  <ProgressBar
                    key={`${linkFilter}-${item.label}`}
                    label={item.label}
                    percentage={item.percentage}
                    width={item.width}
                  />
                ))}
              </div>
            </div>

            {/* Top Location Card */}
            <div className="w-[280px] sm:w-[240px] flex-none space-y-5 rounded-lg bg-white p-4 shadow-sm min-w-[200px] md:w-auto md:flex-1">
              <h3 className="text-[16px] font-bold text-[#475467] md:text-lg">Top Location</h3>

              <FilterTabs
                activeTab={locationFilter}
                tabs={['Cities', 'Countries']}
                onTabChange={(tab) => setLocationFilter(tab)}
              />

              <div className="space-y-3">
                {currentData.topLocation.map((item) => (
                  <ProgressBar
                    key={`${linkFilter}-${item.label}`}
                    label={item.label}
                    percentage={item.percentage}
                    width={item.width}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Conditional Card Display for Overall Filter at Bottom */}
          {linkFilter === 'overall' && (
            /* Link Clicks Card - Shown only for Overall filter at the bottom */
            <div className="rounded-lg bg-white p-5 shadow-sm">
              <DonutChart
                value={currentData.clicks.value}
                label="Link Clicks"
                malePercentage={currentData.clicks.male}
                femalePercentage={currentData.clicks.female}
              />
            </div>
          )}

          {/* CTA Button - Properly contained */}
          <div className="mt-4">
            <Button className="h-12 w-full md:h-14 md:text-lg" variant="primary" type="button">
              Export Analytics
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsPage;